export type TaxRegime = 'NETTING' | 'SINGLE_STAGE';
export type TaxFilingFrequency = 'MONTHLY' | 'QUARTERLY';
export type TaxCategory = 'STANDARD' | 'REDUCED' | 'ZERO_RATED' | 'EXEMPT' | 'OUT_OF_SCOPE';
export type TaxApplicability = 'OUTPUT' | 'INPUT' | 'BOTH';

export interface TaxProfile {
  institutionId: string;
  regime: TaxRegime;
  countryCode: string;
  filingFrequency: TaxFilingFrequency;
  registrationNo?: string | null;
  defaultOutputCodeId?: string | null;
  defaultInputCodeId?: string | null;
  exportCodeId?: string | null;
  settlementAccountId?: string | null;
  refundableAccountId?: string | null;
  nonRecoverableExpenseAccountId?: string | null;
}

export type TaxComponentKind = 'VAT' | 'SUPPLEMENTARY_DUTY' | 'OTHER';

export interface TaxCodeComponent {
  seq?: number;
  name: string;
  ratePct: number;
  compoundOnPrevious: boolean;
  /** VAT when omitted. */
  kind?: TaxComponentKind;
  outputAccountId?: string | null;
  inputAccountId?: string | null;
}

export interface TaxCode {
  id: string;
  institutionId: string;
  code: string;
  name: string;
  category: TaxCategory;
  applicability: TaxApplicability;
  recoverable: boolean;
  active: boolean;
  edited: boolean;
  source: 'MANUAL' | 'BD_DEFAULT';
  note?: string | null;
  effectiveRatePct: number;
  components: TaxCodeComponent[];
}

export interface TaxCodeSave {
  institutionId: string;
  code: string;
  name: string;
  category: TaxCategory;
  applicability: TaxApplicability;
  recoverable: boolean;
  note?: string | null;
  components: Omit<TaxCodeComponent, 'seq'>[];
}

export interface TaxCodeLookupItem {
  id: string;
  code: string;
  name: string;
  category: TaxCategory;
  applicability: TaxApplicability;
  recoverable: boolean;
  active: boolean;
  effectiveRatePct: number;
  summary: string;
}

export interface TaxCodeLookup {
  regime: TaxRegime;
  defaultOutputCodeId?: string | null;
  defaultInputCodeId?: string | null;
  exportCodeId?: string | null;
  codes: TaxCodeLookupItem[];
}

export interface TaxDefaultsResult {
  created: number;
  updated: number;
  unchanged: number;
  skippedEdited: number;
  warnings: string[];
}

export interface TaxReturnComponent { name: string; ratePct: number; taxAmount: number }
export interface TaxReturnRow {
  kind: 'CODE' | 'LEGACY';
  taxCodeId?: string | null;
  code?: string | null;
  label: string;
  category?: TaxCategory | null;
  taxableBase: number;
  taxAmount: number;
  components?: TaxReturnComponent[];
}
export interface TaxCategoryTotal { category: TaxCategory; taxableBase: number; taxAmount: number }

export interface TaxSettlementLine { accountId: string; entryType: string; amount: number; label: string }
export interface TaxPayment {
  id: string;
  paymentDate: string;
  amount: number;
  bankAccountId: string;
  challanNo?: string | null;
  challanDate?: string | null;
  journalEntryId?: string | null;
}
export interface TaxSettlement {
  id: string;
  institutionId: string;
  periodStart: string;
  periodEnd: string;
  status: 'SETTLED' | 'REOPENED';
  outputTax: number;
  recoverableInputTax: number;
  netAmount: number;
  journalEntryId?: string | null;
  reversalJournalEntryId?: string | null;
  settledAt?: string | null;
  reopenedAt?: string | null;
  paidAmount: number;
  outstandingAmount: number;
  lines: TaxSettlementLine[];
  payments: TaxPayment[];
}

/** VAT / SD / other split: input VAT credit offsets VAT only; SD and other duties are payable in full. */
export interface TaxReturnSplit {
  outputVat: number;
  outputSd: number;
  outputOther: number;
  recoverableInputVat: number;
  /** Negative = refundable / carry-forward VAT credit. */
  netVat: number;
  vatPayable: number;
  vatRefundable: number;
  sdPayable: number;
  otherPayable: number;
  /** max(netVat, 0) + SD + other */
  totalPayable: number;
}

export interface TaxReturn {
  institutionId: string;
  periodStart: string;
  periodEnd: string;
  regime: TaxRegime;
  filingFrequency: TaxFilingFrequency;
  output: TaxReturnRow[];
  inputRecoverable: TaxReturnRow[];
  inputNonRecoverable: TaxReturnRow[];
  outputByCategory: TaxCategoryTotal[];
  outputTax: number;
  recoverableInputTax: number;
  nonRecoverableInputTax: number;
  netPayable: number;
  legacyOutputTax: number;
  legacyInputTax: number;
  settlement: TaxSettlement | null;
  changedSinceSettlement: boolean;
  split?: TaxReturnSplit | null;
}

export interface TaxPaymentRequest {
  settlementId: string;
  paymentDate: string;
  amount: number;
  bankAccountId: string;
  challanNo?: string;
  challanDate?: string;
}

// ---- Bangladesh Mushak pack (accounting wave 2, lane B) ----

export type MushakForm = '6.1' | '6.2';

export interface MushakRegisterRow {
  /** LEDGER (coded document), LEGACY (uncoded, free-typed rate) or EXPORT (booked export shipment). */
  source: 'LEDGER' | 'LEGACY' | 'EXPORT';
  date: string;
  partyName: string;
  partyBin: string;
  documentType: string;
  documentNumber: string;
  supplierInvoiceNo: string;
  description: string;
  taxCode: string;
  category: string | null;
  value: number;
  sdRatePct: number;
  sdAmount: number;
  vatRatePct: number;
  vatAmount: number;
  otherTaxAmount: number;
  total: number;
  /** 6.1 only; null on 6.2. */
  recoverable: boolean | null;
  note: string;
}

export interface MushakRegister {
  institutionId: string;
  form: MushakForm;
  institutionName: string;
  bin: string;
  from: string;
  to: string;
  rows: MushakRegisterRow[];
  totals: {
    value: number; sd: number; vat: number; other: number; total: number;
    vatRecoverable: number; vatNonRecoverable: number;
  };
  notes: string[];
}

export interface Mushak91Line {
  key: string;
  label: string;
  value: number;
  sd: number;
  vat: number;
  other: number;
  creditableTax: number | null;
  note: string;
}

export interface Mushak91 {
  institutionId: string;
  institutionName: string;
  bin: string;
  periodStart: string;
  periodEnd: string;
  filingFrequency: TaxFilingFrequency;
  regime: TaxRegime;
  sales: Mushak91Line[];
  purchases: Mushak91Line[];
  summary: {
    outputVat: number; outputSd: number; outputOther: number;
    creditableVat: number; creditableSd: number; creditableOther: number;
    nonRecoverableTax: number;
    netVat: number; netSd: number; netOther: number;
    netPayable: number; broughtForwardCredit: number; netAfterBroughtForward: number;
  };
  settlement: {
    status: string; netAmount: number; paidAmount: number; outstandingAmount: number; changedSinceSettlement: boolean;
  } | null;
  notes: string[];
  // ---- B4-A ----
  vds?: Mushak91Vds | null;
  // ---- end B4-A ----
}

/** Per-component tax of a coded document (empty for legacy documents). */
export interface DocumentTaxComponent {
  name: string;
  kind: 'VAT' | 'SUPPLEMENTARY_DUTY' | 'OTHER';
  ratePct: number;
  taxAmount: number;
  recoverable: boolean;
}
export interface DocumentTaxBreakdown { components: DocumentTaxComponent[] }

// ---- B4-A: Mushak 4.3, 6.10, VDS lines in 9.1 ----
export interface Mushak91VdsLine {
  reference: string; date: string; partyName: string; partyBin: string;
  certificateNumber: string; certificateDate: string | null; challanNumber: string; amount: number;
  deductible: boolean; note: string | null;
}

export interface Mushak91Vds {
  customerNoteRef: string | null; customerDeducted: Mushak91VdsLine[]; customerTotal: number;
  supplierNoteRef: string | null; supplierDeducted: Mushak91VdsLine[]; supplierTotal: number;
  vatSideAfterVds: number; payableAfterVds: number; vatCreditAfterVds: number;
  highValuePurchaseDocuments: number; highValueSalesDocuments: number; highValueThreshold: number;
}

export interface Mushak610Row {
  documentType: string; documentNumber: string; date: string; partyName: string; partyBin: string;
  partyAddress: string; value: number; vat: number; total: number; source: 'LEDGER' | 'HEADER'; note: string | null;
}

export interface Mushak610 {
  institutionId: string; institutionName: string; bin: string; from: string; to: string;
  threshold: number; settingsLoaded: boolean;
  purchases: Mushak610Row[]; sales: Mushak610Row[]; notes: Mushak610Row[]; cancelled: Mushak610Row[];
  purchaseTotal: number; salesTotal: number; remarks: string[];
}

export interface MushakFormSettings {
  institutionId: string; loaded: boolean; thresholdAmount: number;
  vdsCustomerNoteRef: string | null; vdsSupplierNoteRef: string | null;
  form43Footer: string | null; verifyNote: string | null;
}

export type CoefficientStatus = 'DRAFT' | 'SUBMITTED' | 'ACCEPTED' | 'SUPERSEDED';
export type CoefficientProductKind = 'ITEM' | 'STYLE';
export type ValueAddKind = 'WAGES' | 'UTILITIES' | 'RENT' | 'DEPRECIATION' | 'PACKING' | 'TRANSPORT' | 'ADMIN' | 'FINANCE' | 'PROFIT' | 'OTHER';

export const VALUE_ADD_KINDS: ValueAddKind[] =
  ['WAGES', 'UTILITIES', 'RENT', 'DEPRECIATION', 'PACKING', 'TRANSPORT', 'ADMIN', 'FINANCE', 'PROFIT', 'OTHER'];

export interface CoefficientInputLine {
  id?: string | null; itemId: string; itemName: string; uom: string; quantityPerUnit: number;
  wastagePercent: number; wastageQuantity: number; unitPurchasePrice: number; valueAmount: number;
}

export interface CoefficientValueAddLine { id?: string | null; kind: ValueAddKind; amountPerUnit: number; note?: string | null }

export interface CoefficientSummary {
  id: string; productKind: CoefficientProductKind; productName: string; versionNo: number; unitOfSupply: string;
  effectiveFrom: string; supplyPricePerUnit: number; status: CoefficientStatus; vatOfficeReference: string | null;
}

export interface Coefficient extends CoefficientSummary {
  institutionId: string; itemId: string | null; styleId: string | null; hsCode: string | null;
  submissionDate: string | null; acceptedDate: string | null; supersededById: string | null; notes: string | null;
  inputs: CoefficientInputLine[]; valueAdds: CoefficientValueAddLine[];
  inputCostPerUnit: number; valueAddPerUnit: number; totalCostPerUnit: number; marginOverSupplyPrice: number;
}

export interface CoefficientSaveRequest {
  productKind: CoefficientProductKind; itemId?: string | null; styleId?: string | null; unitOfSupply: string;
  hsCode?: string | null; effectiveFrom: string; supplyPricePerUnit: number; notes?: string | null;
  inputs: { itemId: string; quantityPerUnit: number; wastagePercent: number; unitPurchasePrice: number; uom?: string | null }[];
  valueAdds: { kind: ValueAddKind; amountPerUnit: number; note?: string | null }[];
}

export interface CoefficientPrefill {
  productKind: CoefficientProductKind; itemId: string | null; styleId: string | null; productName: string;
  unitOfSupply: string; hsCode: string | null; inputs: CoefficientInputLine[]; warnings: string[];
}

export interface ConsumptionCheckLine {
  styleName: string; itemId: string; itemName: string; uom: string; finishedQuantity: number; declaredQuantity: number;
  allowedQuantity: number; actualQuantity: number; overConsumption: number; flagged: boolean; note: string;
}

export interface ConsumptionCheck {
  buyerOrderId: string; orderNumber: string; lines: ConsumptionCheckLine[]; flaggedCount: number; notes: string[];
}
